import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-jalaali';
import { formatNumber as formatNumberUtil, formatCurrency as formatCurrencyUtil } from '../utils/numberUtils';

type CalendarType = 'persian' | 'gregorian';

interface LocaleContextType {
  locale: string;
  setLocale: (locale: string) => void;
  calendarType: CalendarType;
  setCalendarType: (type: CalendarType) => void;
  isRTL: boolean;
  formatDate: (date: string | Date, format?: string) => string;
  formatNumber: (number: number) => string;
  formatCurrency: (amount: number, currency?: 'IRR' | 'IRT' | 'USD') => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

interface LocaleProviderProps {
  children: React.ReactNode;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<string>(() => {
    // Get saved language from localStorage or default to 'fa'
    const savedLang = localStorage.getItem('i18nextLng') || 'fa';
    return savedLang.startsWith('fa') ? 'fa' : 'en';
  });

  const [calendarType, setCalendarTypeState] = useState<CalendarType>(() => {
    // Get saved calendar type from localStorage or default based on locale
    const savedCalendarType = localStorage.getItem('calendarType') as CalendarType | null;
    if (savedCalendarType === 'persian' || savedCalendarType === 'gregorian') {
      return savedCalendarType;
    }
    // Default: Persian calendar for Persian locale, Gregorian for English
    return locale === 'fa' ? 'persian' : 'gregorian';
  });

  const setLocale = (newLocale: string) => {
    console.log('Setting locale to:', newLocale); // Debug log
    setLocaleState(newLocale);
    
    // Update localStorage
    localStorage.setItem('i18nextLng', newLocale);
    
    // Update i18n
    i18n.changeLanguage(newLocale);
    
    // Update document direction and language
    document.documentElement.dir = newLocale === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLocale;
    
    // Update moment locale based on calendar type
    updateMomentLocale(calendarType);
  };

  const setCalendarType = (type: CalendarType) => {
    console.log('Setting calendar type to:', type); // Debug log
    setCalendarTypeState(type);
    
    // Update localStorage
    localStorage.setItem('calendarType', type);
    
    // Update moment locale
    updateMomentLocale(type);
  };

  const updateMomentLocale = (type: CalendarType) => {
    if (type === 'persian') {
      (moment as any).loadPersian({ dialect: 'persian-modern' });
    } else {
      moment.locale(locale === 'fa' ? 'fa' : 'en');
    }
  };

  const isRTL = locale === 'fa';

  const formatDate = (date: string | Date, format?: string): string => {
    const momentDate = moment(date);
    
    if (calendarType === 'persian') {
      return momentDate.format(format || 'jYYYY/jMM/jDD');
    } else {
      return momentDate.format(format || 'YYYY-MM-DD');
    }
  };

  const formatNumber = (number: number): string => {
    return formatNumberUtil(number, locale as 'fa' | 'en');
  };

  const formatCurrency = (amount: number, currency: 'IRR' | 'IRT' | 'USD' = 'IRR'): string => {
    return formatCurrencyUtil(amount, currency, locale as 'fa' | 'en');
  };

  useEffect(() => {
    console.log('LocaleContext initialized with locale:', locale, 'and calendar type:', calendarType); // Debug log
    
    // Initialize i18n language
    i18n.changeLanguage(locale);
    
    // Initialize moment based on calendar type
    updateMomentLocale(calendarType);
    
    // Set document attributes
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
  }, [locale, calendarType, isRTL, i18n]);

  const value: LocaleContextType = {
    locale,
    setLocale,
    calendarType,
    setCalendarType,
    isRTL,
    formatDate,
    formatNumber,
    formatCurrency,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};
